import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot, RedirectCommand } from '@angular/router';
import { Observable, forkJoin } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';

import { UiService } from '@services/ui.service';
import { WorkflowExecution } from '@classes/workflow/workflow-execution';
import { WorkflowService } from '@services/workflow.service';
import { HttpResponse } from '@angular/common/http';
import { ReferenceMonthService } from '@services/reference-month.service';
import { DateTime } from 'luxon';
import { Workflow } from '@classes/workflow/workflow';

type WorkflowExecutionsResolved =
  | {
      executions: HttpResponse<WorkflowExecution[]>;
      workflows: Workflow[];
    }
  | RedirectCommand;

@Injectable()
export class WorkflowExecutionsResolver implements Resolve<WorkflowExecutionsResolved> {
    constructor(
        private uiSrv: UiService,
        private workflowSrv: WorkflowService,
        private refMonthSrv: ReferenceMonthService
    ) {}

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<WorkflowExecutionsResolved> {
        this.uiSrv.routeLoadingStateChanged.next(true);

        const getData = (refMonth: string | null) => {
            const previousYearRefMonth = refMonth
                ? DateTime.fromISO(refMonth).minus({ year: 1 }).toISO()
                : '';
            return forkJoin({
                executions: this.workflowSrv.getExecutionsList(
                    `referenceMonth>=${previousYearRefMonth}`,
                    '-createdOn',
                    1,
                    10
                ),
                workflows: this.workflowSrv.getWorkflows(),
                statuses: this.workflowSrv.getStatus()
            }).pipe(
                tap(() => this.uiSrv.routeLoadingStateChanged.next(false))
            );
        };

        if (!this.refMonthSrv.refMonth) {
            return this.refMonthSrv.getRefMonth().pipe(
                switchMap(() => getData(this.refMonthSrv.refMonth))
            );
        } else {
            return getData(this.refMonthSrv.refMonth);
        }
    }
}
